// tslint:disable: no-empty
import React, {
	Context,
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import Conversations from '@twilio/conversations'
import { useHistory } from 'react-router-dom'
import { callApi, TApiResponse } from '../api'
import { IChat, TChat, TChatActions } from '../types'
import { useAuthContext } from '../auth'
import { Message } from '@twilio/conversations/lib/message'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialChatState: IChat = {
	chats: [],
	loading: false,
}

const ChatContext: Context<{
	chat: IChat
	actions: TChatActions
}> = createContext({
	chat: initialChatState,
	actions: {
		getChats: (showLoading?: boolean) => {},
		getOrCreateChat: (to: number) => {},
		getUnreadMessagesCount: (msg: Message) => {},
		getToken: () => {},
		updateMessageThread: (id: string, userid: number) => {},
	},
})

export const ChatProvider = (props: PropsWithChildren<{}>) => {
	const [chats, setChats] = useState<TChat[]>([])
	const [chatsCount, setChatsCount] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [twilioToken, setTwilioToken] = useState<string>(null)
	const [twilioConversationsClient, setTwilioConversationsClient] = useState<Conversations>(null)
	const { auth, actions: authActions } = useAuthContext()

	const history = useHistory()

	const timeIdRef = useRef<NodeJS.Timeout | null>(null)

	const getUnreadMessagesCount = useCallback(async (message: Message) => {
		await new Promise((r: (value: unknown) => void) => setTimeout(r, 300))
		if (message?.conversation) {
			let unreadMessagesCount = await message.conversation.getUnreadMessagesCount()
			if (unreadMessagesCount === null) {
				const msgs = await message.conversation.getMessages()
				unreadMessagesCount = msgs.items.length
			}
			setChats((cs: TChat[]) =>
				cs.map((ch: TChat) =>
					ch?.conversation?.sid === message?.conversation?.sid ? { ...ch, unreadMessagesCount } : ch
				)
			)
		}
	}, [])

	const getChats = useCallback(async () => {
		if (!auth.token) return

		setLoading(true)
		const chatResponse: TApiResponse = await callApi(`/chat`, {})

		const { data } = chatResponse.payload

		if (chatResponse.ok) {
			setProviderError(null)
			setChats((data.chats as TChat[]).map((c: TChat) => ({ ...c, unreadMessagesCount: 0 })))
			setTwilioToken(data.twilioToken)
			setLoading(false)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Chats!')
			if (chatResponse.status === 401) {
				authActions.logout()
			}
		}
	}, [auth.token, authActions])

	const getOrCreateChat = useCallback(
		async (to: number) => {
			if (!auth.token) return

			setLoading(true)
			const chatResponse: TApiResponse = await callApi('/chat', {
				method: 'POST',
				body: { to },
			})

			const { data } = chatResponse.payload

			if (chatResponse.ok) {
				setChats([
					{
						...data,
						twilioToken: undefined,
						unreadMessagesCount: 0,
					},
					...chats.filter((c: TChat) => c.id !== data.id),
				])
				history.push(`/messages/${data.id}`)
				setLoading(false)
				setProviderError(null)
			} else {
				setLoading(false)
				setProviderError(data.message || 'Cannot create Chat!')
				if (chatResponse.status === 401) {
					authActions.logout()
				}
			}
		},
		[auth.token, authActions, chats, history]
	)

	const getToken = useCallback(async () => {
		const chatResponse: TApiResponse = await callApi(`/chat/token`, {})

		const { data } = chatResponse.payload

		if (chatResponse.ok) {
			setTwilioToken(data.twilioToken)
		}
	}, [])

	const checkCount = useCallback(async () => {
		clearTimeout(timeIdRef?.current)
		if (!!auth.token) {
			const chatResponse: TApiResponse = await callApi(`/chat/count`, {})

			const { data } = chatResponse.payload

			if (chatResponse.ok) {
				setChatsCount(data.chatsCount)
			}
			timeIdRef.current = setTimeout(checkCount, 60000)
		}
	}, [auth.token])

	const tokenAboutToExpire = useCallback(() => {
		getToken()
	}, [getToken])

	const updateMessageThread = async (id: string, userId: number) => {
		await callApi(`messages/${id}`, {
			method: 'put',
			body: {
				user_id: userId,
				all_read: userId === auth.user.id,
			},
		})
	}

	const actions: TChatActions = { getChats, getOrCreateChat, getUnreadMessagesCount, getToken, updateMessageThread }

	useEffect(() => {
		if (!!auth.token && chatsCount !== chats.length && !loading) {
			actions.getChats()
		}
	}, [chats.length, chatsCount])

	useEffect(() => {
		if (!!auth.token && !timeIdRef?.current) {
			actions.getChats()
			checkCount()
		}

		return () => {
			chats.map(async (c: TChat) => {
				if (c.conversation) c.conversation.removeAllListeners()
			})
			if (twilioConversationsClient) twilioConversationsClient.removeAllListeners()
			setChats([])
			setTwilioConversationsClient(null)
			setTwilioToken(null)

			clearTimeout(timeIdRef.current)
			timeIdRef.current = null
		}
	}, [!!auth.token])

	const addConversationsClient = async (t: string) => {
		const conversationsClient: Conversations = await Conversations.create(t)
		conversationsClient.on('tokenAboutToExpire', tokenAboutToExpire)

		setTwilioConversationsClient(conversationsClient)
	}

	useEffect(() => {
		if (!auth.token || !twilioToken) return

		if (!twilioConversationsClient) {
			addConversationsClient(twilioToken)
		} else {
			twilioConversationsClient.updateToken(twilioToken)
		}
	}, [twilioToken, !!twilioConversationsClient])

	const setChatsWithConversations = async (client: Conversations, currentChats: TChat[]) => {
		const newChats = await Promise.all(
			currentChats.map(
				async (c: TChat): Promise<TChat> => {
					const conv = await client.getConversationBySid(c.twilio)
					conv.on('messageAdded', getUnreadMessagesCount)
					let unreadMessagesCount = await conv.getUnreadMessagesCount()
					if (unreadMessagesCount === null) {
						const msgs = await conv.getMessages()
						unreadMessagesCount = msgs.items.length
					}

					return {
						...c,
						unreadMessagesCount,
						conversation: conv,
					}
				}
			)
		)

		setChats(newChats)
	}

	useEffect(() => {
		if (!twilioConversationsClient) return

		if (!loading) {
			setChatsWithConversations(twilioConversationsClient, chats)
		}
	}, [!!twilioConversationsClient, loading])

	return (
		<ChatContext.Provider value={{ chat: { chats, loading, error }, actions }}>{props.children}</ChatContext.Provider>
	)
}

export const useChatContext: () => { chat: IChat; actions: TChatActions } = () => {
	return useContext(ChatContext)
}
